import React, { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import styles from "./ActiveCallWidget.module.css";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import i18n from "../../../i18n";
import { useAppDispatch, useAppSelector } from "../../../services/hooks";
import {
  currentCallSelector,
  callDurationSelector,
  recordingPausedSelector,
} from "../../../services/selectors/callCenter.selectors";
import { incrementCallDuration } from "../../../services/slices/callCenter.slice";
import { usePaycall } from "../../../contexts/PaycallContext";
import { useSetPaycallRecordingPreferenceMutation } from "../../../services/api/paycall.api";

interface IActiveCallWidgetProps {
  /** Opens the referral / inquiry document drawer (global panel from App shell). */
  onOpenCard: () => void;
  /** True while widget stays visible after remote call end (before it dismisses). */
  isRemoteEndedPhase?: boolean;
}

const ActiveCallWidget: React.FC<IActiveCallWidgetProps> = ({
  onOpenCard,
  isRemoteEndedPhase = false,
}) => {
  const dispatch = useAppDispatch();
  const paycall = usePaycall();
  const currentCall = useAppSelector(currentCallSelector);
  const duration = useAppSelector(callDurationSelector);
  const isPaused = useAppSelector(recordingPausedSelector);

  // Use SIP uniqueid for Paycall recording preference endpoint
  const sipUniqueId = paycall.currentSipCallId;
  
  // Check if this is an outbound call from Redux state
  const isOutboundCall = currentCall?.isOutbound === true;
  const remoteAnswered =
    paycall.outboundRemoteAnswered || currentCall?.remoteAnswered === true;
  const showCallTimer = !isOutboundCall || remoteAnswered;

  const [saveRecording, setSaveRecording] = useState(true);
  const [preferenceError, setPreferenceError] = useState<string | null>(null);
  const [setPreference] = useSetPaycallRecordingPreferenceMutation();
  const saveRecordingRef = useRef(saveRecording);
  saveRecordingRef.current = saveRecording;

  useEffect(() => {
    setSaveRecording(true);
    setPreferenceError(null);
  }, [sipUniqueId]);

  const debouncedSetPreference = useMemo(
    () =>
      debounce(
        async (uniqueId: string, value: boolean, revertTo: boolean) => {
          try {
            await setPreference({
              uniqueId,
              saveRecording: value,
            }).unwrap();
            setPreferenceError(null);
          } catch {
            setSaveRecording(revertTo);
            setPreferenceError(t("CC_RECORDING_PREFERENCE_FAILED"));
          }
        },
        300
      ),
    [setPreference, t]
  );

  useEffect(() => {
    return () => {
      debouncedSetPreference.cancel();
    };
  }, [debouncedSetPreference]);

  const handleRecordingToggle = () => {
    if (!sipUniqueId || isRemoteEndedPhase) return;
    const prev = saveRecordingRef.current;
    const next = !prev;
    setSaveRecording(next);
    setPreferenceError(null);
    debouncedSetPreference(sipUniqueId, next, prev);
  };

  // Timer effect (active call only — not during end animation or outbound dial)
  useEffect(() => {
    if (isRemoteEndedPhase || !showCallTimer) {
      return undefined;
    }
    const interval = setInterval(() => {
      dispatch(incrementCallDuration());
    }, 1000);

    return () => clearInterval(interval);
  }, [dispatch, isRemoteEndedPhase, showCallTimer]);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  if (!currentCall) return null;

  const showRecordingToggle = !isRemoteEndedPhase && Boolean(sipUniqueId);

  const patientName = currentCall.isIdentified && currentCall.callerInfo
    ? `${currentCall.callerInfo.firstName} ${currentCall.callerInfo.lastName}`
    : t("UNKNOWN_CALLER");

  const callCycle =
    currentCall.isIdentified && currentCall.callerInfo
      ? currentCall.callerInfo.cycle ?? currentCall.callerInfo.currentCycle
      : null;

  const headerClassName = isRemoteEndedPhase
    ? styles.headerEnding
    : isOutboundCall && !remoteAnswered
      ? styles.headerOutboundDialing
      : isOutboundCall
        ? styles.headerOutboundConnected
        : "";

  const connectionStatusLabel = remoteAnswered
    ? t("CC_OUTBOUND_CONNECTED")
    : t("CC_OUTBOUND_DIALING");

  return (
    <div
      dir={i18n.dir()}
      className={`${styles.widget} ${isRemoteEndedPhase ? styles.widgetEnding : ""}`}
    >
      {/* Header */}
      <div className={`${styles.header} ${headerClassName}`}>
        <div className={styles.headerMain}>
          <span className={styles.headerText}>
            {isRemoteEndedPhase ? t("CC_CALL_ENDED_PHASE") : t("ACTIVE_CALL")}
          </span>
          {isOutboundCall && !isRemoteEndedPhase ? (
            <span className={styles.outboundBadge}>{t("CC_OUTBOUND_CALL")}</span>
          ) : null}
        </div>
        {isRemoteEndedPhase ? (
          <span className={styles.endedIndicator} aria-hidden>
            ✓
          </span>
        ) : (
          <button
            type="button"
            className={styles.recordingDotButton}
            onClick={() => void paycall.hangup()}
            title={t("END_CALL")}
            aria-label={t("END_CALL")}
          >
            <span className={styles.recordingDot} aria-hidden>
              🔴
            </span>
          </button>
        )}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {isOutboundCall && !isRemoteEndedPhase ? (
          <div
            className={`${styles.connectionStatus} ${
              remoteAnswered
                ? styles.connectionStatusConnected
                : styles.connectionStatusDialing
            }`}
            role="status"
          >
            <span
              className={styles.connectionStatusDot}
              aria-hidden
            />
            {connectionStatusLabel}
          </div>
        ) : null}

        {/* Patient Info */}
        <div className={styles.infoSection}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>{t("SUBJECT")}</span>
            <span className={styles.infoValue}>{patientName}</span>
          </div>
          {callCycle != null && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{t("INTAKE")}</span>
              <span className={styles.infoValue}>{callCycle}</span>
            </div>
          )}
        </div>

        {/* Timer */}
        <div className={styles.timerSection}>
          <div className={styles.timer}>
            {showCallTimer ? formatDuration(duration) : "--:--"}
          </div>
          <div className={styles.timerLabel}>
            {isOutboundCall && !remoteAnswered
              ? t("CC_OUTBOUND_DIALING")
              : t("INTAKE")}
          </div>
        </div>

        {showRecordingToggle && (
          <div className={styles.recordingPreferenceSection}>
            <div
              className={`${styles.recordingPreferenceRow} ${
                saveRecording ? styles.recordingPreferenceRowOn : ""
              }`}
            >
              <button
                type="button"
                className={`${styles.recordingToggle} ${
                  saveRecording ? styles.recordingToggleOn : ""
                }`}
                onClick={handleRecordingToggle}
                aria-pressed={saveRecording}
                aria-label={t("CC_RECORDING_ACTIVE")}
              />
              <span
                className={`${styles.recordingPreferenceLabel} ${
                  saveRecording ? styles.recordingPreferenceLabelOn : ""
                }`}
              >
                {t("CC_RECORDING_ACTIVE")}
              </span>
            </div>

            {!saveRecording && (
              <div className={styles.pauseWarning}>
                {t("CC_RECORDING_WILL_NOT_BE_SAVED")}
              </div>
            )}

            {preferenceError ? (
              <div className={styles.preferenceErrorBanner} role="alert">
                <span className={styles.preferenceErrorIcon} aria-hidden>
                  !
                </span>
                <p className={styles.preferenceErrorText}>{preferenceError}</p>
                <button
                  type="button"
                  className={styles.preferenceErrorDismiss}
                  onClick={() => setPreferenceError(null)}
                  aria-label={t("CLOSE")}
                >
                  ×
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Pause Warning */}
        {!isRemoteEndedPhase && isPaused && (
          <div className={styles.pauseWarning}>
            ⏸ {t("RECORDING_PAUSED")}
          </div>
        )}

        {/* Action Buttons */}
        {!isRemoteEndedPhase && (
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.actionButton} ${styles.buttonEnd}`}
            onClick={() => void paycall.hangup()}
            title={t("END_CALL")}
            aria-label={t("END_CALL")}
          >
            <span className={styles.buttonIcon}>②</span>
            <span className={styles.buttonLabel}>{t("END_CALL")}</span>
          </button>

          <button
            className={`${styles.actionButton} ${styles.buttonOpen}`}
            onClick={onOpenCard}
            title={t("OPEN_CARD")}
          >
            <span className={styles.buttonIcon}>①</span>
            <span className={styles.buttonLabel}>{t("CARD")}</span>
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default ActiveCallWidget;
